import React from 'react';
import { useIntl } from 'react-intl';
import { ChoiceKind } from '../../../../containers/TipDirectPages/Setup/choiceLoad';
import messages from './messages';

// Readout of the post-tour chooser the operator has built, sitting at the head
// of the Setup page above the sections it summarises (card 174). It exists to
// answer one question before they add another link: how many things is the
// customer being asked to choose between, and is "Leave a review" still one of
// a few or one of many?
//
// It leads the page rather than closing it so that an operator who only came in
// to flip a single toggle still reads it. The cost is that it grows by a row
// when a choice is added, shifting the sections below; browser scroll anchoring
// absorbs that, so nothing here may set `overflow-anchor: none`.
//
// It is advice, not a gate. Nothing here blocks a save — operators who want a
// busy screen can still have one, they just get told what it costs.

const kindLabels = {
  [ChoiceKind.reviews]: messages.reviewsLabel,
  [ChoiceKind.social]: messages.socialLabel,
  [ChoiceKind.upsell]: messages.upsellLabel
};

const ChoiceAdvisor = ({ summary }) => {
  const intl = useIntl();
  const { choices, total, crowded, skipsChooser, reviewsOn, busiestTour, tourLinksCrowded } =
    summary;

  // Amber is reserved for the crowded case so the calm state reads as a summary
  // rather than a standing complaint about a setup that is fine.
  const tone = crowded
    ? {
        card: 'bg-amber-50 border-amber-200',
        heading: 'text-amber-900',
        body: 'text-amber-900',
        chip: 'bg-white border-amber-200 text-amber-900'
      }
    : {
        card: 'bg-slate-50 border-slate-200',
        heading: 'text-slate-800',
        body: 'text-slate-500',
        chip: 'bg-white border-slate-200 text-slate-600'
      };

  const labelFor = choice =>
    choice.kind === ChoiceKind.customLink
      ? choice.label
      : intl.formatMessage(kindLabels[choice.kind]);

  return (
    <div
      className={`flex flex-col p-6 rounded-3xl shadow-sm border border-solid w-full h-fit ${tone.card}`}>
      <div className={`text-xl font-bold mb-3.5 ${tone.heading}`}>
        {intl.formatMessage(messages.title)}
      </div>

      <div className={`text-sm ${tone.body}`} aria-live="polite">
        {total === 0
          ? intl.formatMessage(messages.empty)
          : intl.formatMessage(messages.count, { count: total })}
      </div>

      {total > 0 && (
        <ul className="flex flex-wrap gap-2 list-none p-0 mt-4 mb-0">
          {choices.map((choice, index) => (
            <li
              key={`${choice.kind}-${index}`}
              className={`text-sm font-medium rounded-xl border border-solid px-3 py-1.5 ${tone.chip}`}>
              {labelFor(choice)}
            </li>
          ))}
        </ul>
      )}

      {crowded && (
        <p className={`text-sm mt-4 mb-0 ${tone.body}`}>{intl.formatMessage(messages.crowded)}</p>
      )}

      {skipsChooser && (
        <p className={`text-sm mt-4 mb-0 ${tone.body}`}>
          {intl.formatMessage(messages.straightThrough)}
        </p>
      )}

      {total > 0 && !reviewsOn && (
        <p className={`text-sm mt-4 mb-0 ${tone.body}`}>
          {intl.formatMessage(messages.reviewsOff)}
        </p>
      )}

      {tourLinksCrowded && (
        <p className={`text-sm mt-4 mb-0 ${tone.body}`}>
          {intl.formatMessage(messages.tourLinks, {
            tour: busiestTour.title,
            count: busiestTour.links
          })}
        </p>
      )}

      {/* Smaller than the rest but still on the body colour: slate-400 on
          slate-50 lands around 2.8:1, under the AA floor for body text. */}
      {total > 0 && (
        <p className={`text-xs mt-4 mb-0 ${tone.body}`}>
          {intl.formatMessage(messages.guideLinks)}
        </p>
      )}
    </div>
  );
};

export default ChoiceAdvisor;
